import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkInspections} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {FieldEditor} from 'src/pages/Modules/Inspections/FieldEditor/FieldEditor';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {EquipmentTypeDocumentTitle} from 'src/pages/Modules/Inspections/shared/equipmentTypeHelpers';
import {EquipmentTypeForm} from 'src/pages/Modules/Inspections/shared/EquipmentTypeForm';
import {useGetEquipmentTypeQuery} from 'src/redux/queries/equipmentType';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

type EquipmentTypeEditTab = 'details' | 'fields';

export const EquipmentTypeEditPage = () => {
  useSetDocumentTitle(EquipmentTypeDocumentTitle);
  const navigate = useAppNavigate();
  const {equipmentTypeId, view} = useParams();
  const id = Number(equipmentTypeId);

  const {data: item} = useGetEquipmentTypeQuery(id, {skip: !id});

  const {RenderedTabsComponent} = useUrlQueryTabV2<EquipmentTypeEditTab>({
    view: (view ?? '') as EquipmentTypeEditTab,
    defaultView: 'details',
    tabs: [
      {
        view: 'details',
        label: 'Details',
        component: <EquipmentTypeForm id={id} mode="edit" showHeader={false} />,
      },
      {
        view: 'fields',
        label: 'Inspection Fields',
        component: (
          <FieldEditor equipmentTypeId={id} equipmentTypeName={item?.name} />
        ),
      },
    ],
    onSelectView: nextView =>
      navigate(
        PathUtility.EquipmentTypesEdit(id, nextView as EquipmentTypeEditTab),
        {replace: true}
      ),
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
      <BackBar
        fallbackTo={PathUtility.EquipmentTypesView(id)}
        fallbackLabel="Back to Equipment Type"
        breadcrumbs={[
          {
            label: EquipmentTypeDocumentTitle,
            to: PathUtility.Inspections('equipment-types'),
          },
          {
            label: item?.name ?? 'Equipment Type',
            to: PathUtility.EquipmentTypesView(id),
          },
          {label: 'Edit'},
        ]}
      />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkInspections.icon}>
          {item?.name ?? 'Edit Equipment Type'}
        </PageHeader.Title>

        <PageHeader.Badges>
          <PageHeader.Badge color="yellow" variant="light">
            Editing
          </PageHeader.Badge>
        </PageHeader.Badges>
      </PageHeader>

      {RenderedTabsComponent}
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.EquipmentTypesEdit(':equipmentTypeId', ':view?'),
  element: <EquipmentTypeEditPage />,
  module: ModuleEnum.Inspections,
  operation: PermissionOperationEnum.Update,
  isPrivate: true,
});
